/**
 * Admin orders and transactions (A-16): every order and where it stands, every payment and refund,
 * the period's totals, and the downloads for filing — the register as CSV / Excel / PDF, GSTR-1
 * JSON, and Tally XML vouchers.
 *
 * Periods are IST days ("2026-10-01"), both ends included.
 */

import { apiFetch, fetchBlobUrl } from "./client";
import type { Refund } from "./payments";

// --- Orders ---

export interface AdminOrder {
  id: string;
  order_id: string; // the reference people quote, e.g. ORD-12
  created_at: string;
  status_code: string;
  status_label: string;
  status_final: boolean;
  patient_name: string | null;
  patient_phone: string | null;
  city: string | null;
  state_code: string | null;
  line_count: number;
  final_amount: string;
  pricing_published_at: string | null; // until then the amounts are placeholders
  paid_at: string | null;
  cancelled_at: string | null;
  refunded_amount: string;
  assignee_name: string | null;
}

export interface PaginatedAdminOrders {
  data: AdminOrder[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export type OrderPaymentFilter = "" | "paid" | "unpaid" | "refunded";
export type OrderSort = "placed" | "amount" | "paid" | "patient";

export interface AdminOrderQuery {
  q?: string;
  status?: string;
  payment?: OrderPaymentFilter;
  placed_from?: string;
  placed_to?: string;
  sort?: OrderSort;
  descending?: boolean;
  page?: number;
  per_page?: number;
}

export const listAdminOrders = (query: AdminOrderQuery = {}): Promise<PaginatedAdminOrders> => {
  const params = new URLSearchParams();
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.status) params.set("status", query.status);
  if (query.payment) params.set("payment", query.payment);
  if (query.placed_from) params.set("placed_from", query.placed_from);
  if (query.placed_to) params.set("placed_to", query.placed_to);
  if (query.sort) params.set("sort", query.sort);
  params.set("descending", String(query.descending ?? true));
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/admin/orders?${params.toString()}`);
};

export interface AdminOrderLine {
  medicine_name: string;
  hsn_code: string | null;
  quantity_prescribed: number;
  quantity_dispensed: number;
  is_available: boolean;
  quantity_remark: string | null;
  mrp_per_unit: string;
  taxable_value: string;
  gst_pct: string;
  cgst_amount: string;
  sgst_amount: string;
  igst_amount: string;
}

export interface AdminOrderEvent {
  at: string;
  from_label: string | null;
  to_label: string;
  actor_role: string;
  actor_name: string | null;
  remark: string | null;
}

export interface AdminOrderPayment {
  id: string;
  status: string;
  amount: string;
  initiated_at: string;
  completed_at: string | null;
  rzp_order_id: string;
  rzp_payment_id: string | null;
  failure_reason: string | null;
}

export interface AdminOrderDetail extends AdminOrder {
  for_family_member_name: string | null;
  delivery_address: Record<string, unknown>;
  placement_note: string | null;
  medicines_subtotal: string;
  delivery_charge: string;
  gst_total: string;
  lines: AdminOrderLine[];
  history: AdminOrderEvent[]; // oldest first
  payments: AdminOrderPayment[]; // newest first
  refunds: Refund[]; // newest first
}

export const getAdminOrder = (id: string): Promise<AdminOrderDetail> => apiFetch(`/api/v1/admin/orders/${id}`);

// --- Transactions ---

export interface Transaction {
  id: string;
  kind: "payment" | "refund";
  status: string; // payment: pending | succeeded | failed | expired | cancelled; refund: initiated | processed | failed
  at: string;
  amount: string; // positive either way; `kind` says which way the money went
  order_uuid: string;
  order_id: string;
  patient_name: string | null;
  patient_phone: string | null;
  rzp_order_id: string | null;
  rzp_payment_id: string | null;
  rzp_refund_id: string | null;
  note: string | null; // a refund's reason, or why a payment failed
  initiated_by_name: string | null;
}

export interface PaginatedTransactions {
  data: Transaction[];
  total: number;
  page: number;
  per_page: number;
  has_more: boolean;
}

export type TransactionType = "" | "payment" | "refund";
export type TransactionStatus = "" | "completed" | "pending" | "failed";

export interface TransactionQuery {
  type?: TransactionType;
  status?: TransactionStatus;
  q?: string;
  date_from?: string;
  date_to?: string;
  sort?: "at" | "amount";
  descending?: boolean;
  page?: number;
  per_page?: number;
}

export const listTransactions = (query: TransactionQuery = {}): Promise<PaginatedTransactions> => {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.status) params.set("status", query.status);
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.date_from) params.set("date_from", query.date_from);
  if (query.date_to) params.set("date_to", query.date_to);
  if (query.sort) params.set("sort", query.sort);
  params.set("descending", String(query.descending ?? true));
  params.set("page", String(query.page ?? 1));
  params.set("per_page", String(query.per_page ?? 25));
  return apiFetch(`/api/v1/admin/transactions?${params.toString()}`);
};

/** Payments taken and refunds paid under the filters, and the GST on the orders paid in the period. */
export interface TransactionsSummary {
  payments_count: number;
  payments_amount: string;
  refunds_count: number;
  refunds_amount: string;
  net_amount: string;
  invoices_count: number;
  taxable_value: string;
  cgst: string;
  sgst: string;
  igst: string;
  gst_total: string;
}

export const transactionsSummary = (query: TransactionQuery = {}): Promise<TransactionsSummary> => {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.status) params.set("status", query.status);
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.date_from) params.set("date_from", query.date_from);
  if (query.date_to) params.set("date_to", query.date_to);
  return apiFetch(`/api/v1/admin/transactions/summary?${params.toString()}`);
};

// --- Downloads ---

export type ReportFormat = "csv" | "xlsx" | "pdf" | "gstr1" | "tally";

/** The Tally ledgers vouchers post to (they must exist in Tally under these names); blank = default. */
export interface TallyLedgerNames {
  company: string;
  party: string;
  sales: string;
  delivery: string;
  cgst: string;
  sgst: string;
  igst: string;
  refunds: string;
}

/** Downloads a report and saves it under the server's file name, which it returns. */
export async function downloadTransactionReport(
  format: ReportFormat,
  query: TransactionQuery,
  tally?: Partial<TallyLedgerNames>,
): Promise<string> {
  const params = new URLSearchParams();
  if (query.type) params.set("type", query.type);
  if (query.status) params.set("status", query.status);
  if (query.q?.trim()) params.set("q", query.q.trim());
  if (query.date_from) params.set("date_from", query.date_from);
  if (query.date_to) params.set("date_to", query.date_to);
  if (tally?.company?.trim()) params.set("tally_company", tally.company.trim());
  if (tally?.party?.trim()) params.set("tally_party", tally.party.trim());
  if (tally?.sales?.trim()) params.set("tally_sales", tally.sales.trim());
  if (tally?.delivery?.trim()) params.set("tally_delivery", tally.delivery.trim());
  if (tally?.cgst?.trim()) params.set("tally_cgst", tally.cgst.trim());
  if (tally?.sgst?.trim()) params.set("tally_sgst", tally.sgst.trim());
  if (tally?.igst?.trim()) params.set("tally_igst", tally.igst.trim());
  if (tally?.refunds?.trim()) params.set("tally_refunds", tally.refunds.trim());
  // As a blob of a type browsers only ever download (client.fetchBlobUrl), never opened in place.
  const { url, filename } = await fetchBlobUrl(`/api/v1/admin/transactions/export?format=${format}&${params.toString()}`);
  const name = filename || `transactions.${format === "gstr1" ? "json" : format === "tally" ? "xml" : format}`;
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // After the click has handed the file to the browser.
    setTimeout(() => URL.revokeObjectURL(url), 30_000);
  }
  return name;
}
